import { FRAME_MS, GUARD_TONES, TONES, checkSampleRate } from "./codec";
import { FFT_SIZE, chooseSymbol, createDecoder, createLevelReader } from "./detector";
import workletUrl from "./detector.worklet.js?url";
import { AUDIO_NOT_RUNNING } from "./emitter";

const SIGNAL_EVERY_MS = 500; // how often the signal strength is reported

const createContext = () => new (window.AudioContext || window.webkitAudioContext)();

/**
 * Listens for the host's tones on the microphone and reports decoded messages. Detection
 * runs on the audio thread (an AudioWorklet), so it is not slowed down when the page's
 * timers are throttled; without worklet support it polls an AnalyserNode instead.
 * `source` (an AudioNode) replaces the microphone, which lets the whole chain be checked in
 * one page by connecting an emitter to it. `onSignal` hears the strongest tone above the
 * noise floor (dB) a couple of times a second; `onContextState` hears about the audio
 * context being interrupted or running again, and about the microphone going away.
 *
 * A listener is single-use: once stopped it stays stopped, and anything that arrives late
 * (the microphone, the worklet module, a message) is discarded.
 */
export function createListener({
  sessionCode,
  onMessage,
  onSignal,
  onFrame, // optional diagnostic trace: decided symbols and audio timestamps, never microphone samples
  onContextState,
  audioContext,
  source,
  frameMs = FRAME_MS,
  useWorklet = true,
  now = () => performance.now(),
}) {
  let ctx = audioContext || null;
  const ownsContext = !audioContext;
  let stream = null;
  let nodes = [];
  let timer = null;
  let stopped = false;
  let failed = false;
  let inputConnection = null;
  let stateHandler = null;
  let lastSignalAt = -Infinity;
  const decoder = createDecoder({
    sessionCode,
    onMessage: (message) => {
      if (!stopped) onMessage(message);
    },
  });

  const interrupted = (restart = false) => {
    if (stopped) return;
    failed ||= restart;
    decoder.reset();
    if (onSignal) onSignal(0);
    if (onContextState) onContextState("interrupted");
  };

  const inputAvailable = () => !failed && (!stream || stream.getTracks().every((track) => track.readyState !== "ended" && !track.muted));

  const disconnectInput = () => {
    if (!inputConnection) return;
    try { inputConnection.input.disconnect(inputConnection.output); } catch { /* already disconnected */ }
    inputConnection = null;
  };

  /** One reading of tone levels: fed to the decoder, and summarised for the signal meter. */
  const handleLevels = (levels, timeMs) => {
    if (stopped || !inputAvailable()) return;
    const symbol = chooseSymbol(levels);
    if (onFrame) onFrame({ symbol, time: timeMs });
    decoder.feed(symbol, timeMs);
    if (onSignal && timeMs - lastSignalAt >= SIGNAL_EVERY_MS) {
      lastSignalAt = timeMs;
      const tones = levels?.tones || [];
      const peak = tones.length ? Math.max(...tones) - levels.floor : 0;
      onSignal(Number.isFinite(peak) ? Math.max(0, peak) : 0);
    }
  };

  const watch = (context) => {
    if (onContextState && "onstatechange" in context) {
      stateHandler = () => {
        if (ctx !== context || stopped) return;
        if (context.state !== "running" || !inputAvailable()) interrupted(context.state === "closed");
        else onContextState("running");
      };
      context.onstatechange = stateHandler;
    }
  };

  const startWorklet = async (input) => {
    await ctx.audioWorklet.addModule(workletUrl);
    if (stopped) return;
    const worklet = new AudioWorkletNode(ctx, "sound-sync-detector", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      processorOptions: { tones: TONES, guardTones: GUARD_TONES, windowMs: frameMs },
    });
    worklet.port.onmessage = (event) => {
      if (stopped || !inputAvailable()) return;
      const levels = event.data;
      // Audio-thread timestamps expose a main-thread backlog. Old packets are not fresh
      // host selections, even if their checksum still matches.
      if (!Number.isFinite(levels.time) || ctx.state !== "running" || ctx.currentTime * 1000 - levels.time > 250) {
        decoder.reset();
        if (onSignal) onSignal(0);
        return;
      }
      if (levels.reset) {
        // An upstream oscillator ending can make the browser supply no input channel.
        // Finish a confirmed final symbol before discarding any incomplete packet.
        decoder.feed(null, levels.time);
        decoder.reset();
        if (onSignal) onSignal(0);
        return;
      }
      handleLevels(levels, levels.time);
    };
    worklet.onprocessorerror = () => {
      interrupted(true); // a processor that throws cannot be repaired by context.resume()
    };
    // a worklet only runs while it is connected towards the destination; keep it silent
    const mute = ctx.createGain();
    mute.gain.value = 0;
    nodes.push(worklet, mute);
    inputConnection = { input, output: worklet };
    input.connect(worklet);
    worklet.connect(mute);
    mute.connect(ctx.destination);
  };

  const startPolling = (input) => {
    const analyser = ctx.createAnalyser();
    analyser.fftSize = FFT_SIZE;
    analyser.smoothingTimeConstant = 0;
    inputConnection = { input, output: analyser };
    input.connect(analyser);
    const buffer = new Float32Array(analyser.frequencyBinCount);
    const read = createLevelReader({ sampleRate: ctx.sampleRate, fftSize: FFT_SIZE });
    timer = setInterval(() => {
      analyser.getFloatFrequencyData(buffer);
      handleLevels(read(buffer), now());
    }, frameMs);
    nodes.push(analyser);
  };

  return {
    /**
     * Asks for the microphone (unless a source was given) and starts decoding. Rejects
     * (UNSUPPORTED_SAMPLE_RATE) when the audio input cannot carry the tones.
     */
    async start() {
      if (stopped) return;
      if (!ctx) ctx = createContext();
      watch(ctx);
      if (ctx.state !== "running" && typeof ctx.resume === "function") await ctx.resume();
      if (stopped) return;
      if (ctx.state !== "running") throw Object.assign(new Error("Audio could not start. Tap Try again."), { name: AUDIO_NOT_RUNNING });
      checkSampleRate(ctx.sampleRate);

      let input = source;
      if (!input) {
        const media = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        });
        if (stopped) {
          // stopped while the permission prompt was open: do not keep the microphone
          media.getTracks().forEach((track) => track.stop());
          return;
        }
        stream = media;
        input = ctx.createMediaStreamSource(stream);
        nodes.push(input);
        // the microphone taken away (a call, a headset unplugged) ends its track
        media.getTracks().forEach((track) => {
          track.onended = () => {
            interrupted(true);
          };
          track.onmute = () => interrupted();
          track.onunmute = () => { if (stateHandler) stateHandler(); };
        });
      }

      decoder.reset();
      if (useWorklet && ctx.audioWorklet && typeof AudioWorkletNode !== "undefined") {
        try {
          await startWorklet(input);
          if (!stopped && (ctx.state !== "running" || !inputAvailable())) throw new Error("Audio input was interrupted during startup. Try again.");
          return;
        } catch (error) {
          if (stopped) return;
          if (ctx.state !== "running" || !inputAvailable()) throw error;
          // the worklet module could not be loaded (blocked worker scripts, a stale cache):
          // polling is slower in a hidden tab but works everywhere
          console.warn("Sound sync: falling back to polling", error);
          disconnectInput();
          nodes.filter((node) => node !== input).forEach((node) => node.disconnect());
          nodes = nodes.filter((node) => node === input);
        }
      }
      if (stopped) return;
      startPolling(input);
    },
    /** After an interruption: tries to get the context running again. Resolves with whether it is. */
    async resume() {
      const context = ctx;
      if (!context || stopped || !inputAvailable()) return false;
      decoder.reset();
      if (context.state !== "running" && typeof context.resume === "function") await context.resume();
      return !stopped && ctx === context && inputAvailable() && context.state === "running";
    },
    get needsRestart() { return failed || ctx?.state === "closed"; },
    stop() {
      stopped = true;
      clearInterval(timer);
      timer = null;
      if (stream) {
        stream.getTracks().forEach((track) => {
          track.onended = track.onmute = track.onunmute = null;
          track.stop();
        });
        stream = null;
      }
      disconnectInput();
      for (const node of nodes) {
        if (node.port) node.port.onmessage = null;
        if ("onprocessorerror" in node) node.onprocessorerror = null;
        try {
          node.disconnect();
        } catch {
          // already gone
        }
      }
      nodes = [];
      if (ctx && ctx.onstatechange === stateHandler) ctx.onstatechange = null;
      if (ownsContext && ctx) {
        if (typeof ctx.close === "function") Promise.resolve(ctx.close()).catch(() => {});
        ctx = null;
      }
    },
    /** Feeds one reading of tone levels by hand (tests). */
    feedLevels(levels, timeMs) {
      handleLevels(levels, timeMs);
    },
  };
}
